import * as Yup from 'yup';
import {
  nonWhiteSpaceValidator,
  requiredDateValidator,
  requiredNumberValidator,
} from '@gokrew/ui/utility/validators';
import { twoDecimals } from '@gokrew/ui/utility/utility';
import { GrantRecipientScope, LeaveDuration } from '@gokrew/models/leaves';
import type { TranslationNS } from '@gokrew/models/constants';
import type { TFunction } from 'i18next';

export const leaveHolidaySchema = (
  t: TFunction<TranslationNS.LeaveManagement>
) =>
  Yup.object({
    name: Yup.string().trim().required(t('validation.holiday.holiday_name')),
    leaveDate: requiredDateValidator(
      t('validation.holiday.leave_date.required')
    ).typeError(t('validation.holiday.leave_date.invalid')),
    description: nonWhiteSpaceValidator(t('validation.holiday.description')),
  });

export const leaveHolidaysSchema = (
  t: TFunction<TranslationNS.LeaveManagement>
) =>
  Yup.object({
    holidays: Yup.array()
      .of(leaveHolidaySchema(t))
      .min(1, t('validation.holiday.min')),
  });

export const oneTimeAllowanceSchema = (
  t: TFunction<TranslationNS.LeaveManagement>
) =>
  Yup.object({
    policyId: Yup.string().required(t('validation.one_time_allowance.policy')),

    policyLeaveType: Yup.string().required(
      t('validation.one_time_allowance.leave_type')
    ),

    recipientScope: Yup.mixed<GrantRecipientScope>()
      .oneOf(Object.values(GrantRecipientScope))
      .required(),

    employeeIds: Yup.array()
      .of(Yup.string())
      .when('recipientScope', {
        is: GrantRecipientScope.Employees,
        then: (schema) =>
          schema
            .min(1, t('validation.one_time_allowance.employees.min'))
            .required(t('validation.one_time_allowance.employees.required')),
      }),

    branchIds: Yup.array()
      .of(Yup.string())
      .when('recipientScope', {
        is: GrantRecipientScope.Branches,
        then: (schema) =>
          schema.min(1, t('validation.one_time_allowance.branches.min')),
      }),

    departmentIds: Yup.array()
      .of(Yup.string())
      .when('recipientScope', {
        is: GrantRecipientScope.Departments,
        then: (schema) =>
          schema.min(1, t('validation.one_time_allowance.departments.min')),
      }),

    effectiveDate: Yup.string().required(
      t('validation.one_time_allowance.effective_date.required')
    ),

    // Removing days is a correction: it must say why (the API checks too).
    remark: Yup.string().when('amount', {
      is: (amount: number | null | undefined) => amount != null && amount < 0,
      then: (schema) =>
        schema
          .trim()
          .required(t('validation.one_time_allowance.remark.required')),
      otherwise: (schema) => schema.optional(),
    }),

    amount: requiredNumberValidator(
      t('validation.one_time_allowance.allowance.required')
    )
      .nonNullable(t('validation.one_time_allowance.allowance.required'))
      .typeError(t('validation.one_time_allowance.allowance.type'))
      .min(-365, t('validation.one_time_allowance.amount.min'))
      .max(365, t('validation.one_time_allowance.amount.max'))
      .notOneOf([0], t('validation.one_time_allowance.allowance.not_zero'))
      .test(
        'two-decimals',
        t('validation.one_time_allowance.allowance.max_decimals'),
        (v) => (v == null ? true : twoDecimals(v))
      ),
  });

//Schema for request leave form
export const companyRequestLeaveSchema = (
  t: TFunction<'leave-request-management'>,
  wallets: { id: string; reasonRequired?: boolean }[] = []
) =>
  Yup.object().shape({
    employeeId: Yup.string().required(
      t('validation_schema.company_leave_request.employee_required')
    ),

    leaveWalletId: Yup.string().required(
      t('validation_schema.company_leave_request.leave_type_required')
    ),

    leaveDuration: Yup.mixed<LeaveDuration>()
      .oneOf(Object.values(LeaveDuration))
      .required(
        t('validation_schema.company_leave_request.leave_duration_required')
      ),

    leaveDate: Yup.date().when('leaveDuration', {
      is: (d: LeaveDuration) =>
        d === LeaveDuration.FULL_DAY ||
        d === LeaveDuration.HALF_DAY ||
        d === LeaveDuration.HOURLY,
      then: (schema) =>
        schema
          .typeError(t('validation_schema.company_leave_request.dates.invalid'))
          .required(
            t(
              'validation_schema.company_leave_request.dates.leave_date_required'
            )
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    meridiem: Yup.string().when('leaveDuration', {
      is: LeaveDuration.HALF_DAY,
      then: (schema) =>
        schema.required(
          t('validation_schema.company_leave_request.meridiem_required')
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    startTime: Yup.string().when('leaveDuration', {
      is: LeaveDuration.HOURLY,
      then: (schema) =>
        schema
          .required(
            t('validation_schema.company_leave_request.start_time_required')
          )
          .matches(
            /^([01]\d|2[0-3]):[0-5]\d$/,
            t('validation_schema.company_leave_request.invalid_time_format')
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    endTime: Yup.string().when('leaveDuration', {
      is: LeaveDuration.HOURLY,
      then: (schema) =>
        schema
          .required(
            t('validation_schema.company_leave_request.end_time_required')
          )
          .matches(
            /^([01]\d|2[0-3]):[0-5]\d$/,
            t('validation_schema.company_leave_request.invalid_time_format')
          )
          .test(
            'after-start',
            t('validation_schema.company_leave_request.end_time_after_start'),
            function (endTime) {
              const { startTime } = this.parent;
              if (!startTime || !endTime) return true;
              return endTime > startTime;
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    startDate: Yup.date().when('leaveDuration', {
      is: LeaveDuration.MULTI_DAY,
      then: (schema) =>
        schema
          .typeError(t('validation_schema.company_leave_request.dates.invalid'))
          .required(
            t(
              'validation_schema.company_leave_request.dates.start_date_required'
            )
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    endDate: Yup.date().when(
      ['leaveDuration', 'startDate'],
      (values, schema) => {
        const [leaveDuration, startDate] = values as [
          LeaveDuration,
          Date | undefined,
        ];

        if (leaveDuration !== LeaveDuration.MULTI_DAY) {
          return schema.notRequired();
        }

        let result = schema
          .typeError(t('validation_schema.company_leave_request.dates.invalid'))
          .required(
            t('validation_schema.company_leave_request.dates.end_date_required')
          );

        if (startDate) {
          result = result.min(
            startDate,
            t('validation_schema.company_leave_request.dates.end_date_min')
          );
        }

        return result;
      }
    ),

    // Required when the picked type says so (JP, 2026-10-22): the note is
    // for the record, whoever types it, HR included.
    reason: Yup.string().when('leaveWalletId', ([leaveWalletId], schema) =>
      wallets.find((w) => w.id === leaveWalletId)?.reasonRequired
        ? schema
            .trim()
            .required(
              t('validation_schema.company_leave_request.reason_required')
            )
        : schema.optional()
    ),
  });

export const createPolicySchema = (
  t: TFunction<TranslationNS.LeaveManagement>
) =>
  Yup.object().shape({
    policyName: Yup.string()
      .required(t('validation.policy.name.required'))
      .trim()
      .min(3, t('validation.policy.name.min')),
    description: Yup.string(),
    startMode: Yup.string()
      .oneOf(['Immediate', 'Scheduled'])
      .required(t('validation.policy.start_mode')),
    scheduledStartDate: Yup.string().when('startMode', {
      is: 'Scheduled',
      then: (schema) =>
        schema.required(t('validation.policy.scheduled_start_date')),
      otherwise: (schema) => schema.notRequired(),
    }),

    scheduledEndDate: Yup.string()
      .nullable()
      .test(
        'is-after-start',
        t('validation.policy.scheduled_end_date.after_start'),
        function (value) {
          const { scheduledStartDate } = this.parent;
          if (!value || !scheduledStartDate) return true;
          return new Date(value) >= new Date(scheduledStartDate);
        }
      ),

    branchIds: Yup.array()
      .of(Yup.string())
      .min(1, t('validation.policy.branches.min'))
      .required(t('validation.policy.branches.min')),

    policyLeaveTypes: Yup.array()
      .min(1, t('validation.policy.leave_types.min'))
      .required(),
  });
